import { createFileRoute, Link } from "@tanstack/react-router";
import { DarkCtaSection } from "@/components/dark-cta-section";

const pageTitle = "Understanding Agentforce & AELA Agreements";
const pageDesc =
  "Salesforce AELA procurement and commercial advisory, independent of Salesforce, for enterprises evaluating Agentic Enterprise License Agreements, AI consumption and long-term contract strategy.";
const searchDescription = "Independent Salesforce AELA advice for enterprises assessing Agentic Enterprise License Agreements, Agentforce consumption and contract strategy.";

const faqData = [
  {
    question: "What is a Salesforce AELA?",
    answer:
      "AELA stands for Agentic Enterprise License Agreement. Salesforce describes it as a fixed-price, fixed-term agreement that gives an organisation unlimited access to covered Salesforce consumption services, without usage-based billing or overage charges under that model. It is aimed at organisations scaling AI and data across the enterprise and is intended to make spend more predictable. The products, entitlements and scope covered are defined by each individual agreement.",
  },
  {
    question: "How is AELA different from consumption-based Salesforce pricing?",
    answer:
      "With consumption-based pricing, usage is metered and spend can move up or down with actual consumption, which makes budgeting harder as AI and data workloads grow. Under an AELA, the price and term are fixed and covered consumption services are accessed without overage charges, so the commercial exposure is agreed up front rather than accumulated through usage.",
  },
  {
    question: "Does AELA mean unlimited Salesforce usage?",
    answer:
      "Not automatically. Salesforce positions AELA as unlimited access to the consumption services that the agreement covers, which is not the same as unlimited access to everything Salesforce sells. What counts as covered, and any product-specific conditions or exclusions, depends entirely on the individual contract, so the definition of unlimited should be read carefully before signing.",
  },
  {
    question: "What products can an AELA cover?",
    answer:
      "Salesforce discusses AELA in connection with consumption services including Agentforce, Data 360 (Data Cloud) and, in enterprise contexts, MuleSoft. No AELA automatically includes all of these. The exact products, entitlements and scope are set out in the individual agreement.",
  },
  {
    question: "How do Flex Credits relate to AELA?",
    answer:
      "Flex Credits are part of Salesforce's broader consumption and buying ecosystem, and the Digital Wallet can provide visibility into how consumption is drawn down. An AELA is a different thing: an enterprise commercial agreement providing unlimited access to covered consumption services for a fixed price and term. AELA is not simply the purchase of a pool of Flex Credits, and how consumption is treated commercially depends on the customer's agreement.",
  },
  {
    question: "Is AELA the same as SELA?",
    answer:
      "No. A SELA is an enterprise agreement structure historically associated with broad, largely seat-based Salesforce licensing, while AELA is an agreement built around unlimited access to covered consumption services for a fixed price and term. AELA is not a renamed SELA, and it does not automatically replace an existing SELA.",
  },
  {
    question: "What should procurement review before signing an AELA?",
    answer:
      "The essentials are scope (what is covered and what is excluded), the precise definition of unlimited, the initial term and renewal mechanics, what is price-protected and what can change, how new Salesforce AI and data capabilities are treated, how much commercial flexibility exists if adoption differs from the forecast, and what happens at the end of the agreement. These are questions to ask, not terms Salesforce guarantees: the answers are agreement-specific.",
  },
  {
    question: "Can an existing Salesforce agreement be moved to AELA?",
    answer:
      "There is no universal conversion process, and no Salesforce customer should assume an automatic switch. Whether a move is possible, and on what commercial terms, is a negotiation shaped by the current contract, its timing and the organisation's AI and data plans. We model the scenarios before that conversation starts.",
  },
];

const jsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "WebPage",
      "@id": "https://saased.co/aela#webpage",
      url: "https://saased.co/aela",
      name: `${pageTitle} | SaaSed`,
      description: pageDesc,
      about: {
        "@type": "Thing",
        name: "Salesforce Agentic Enterprise License Agreement (AELA)",
      },
    },
    {
      "@type": "Service",
      "@id": "https://saased.co/aela#service",
      name: "Salesforce AELA Procurement & Commercial Advisory",
      provider: { "@id": "https://saased.co/#organization" },
      description: pageDesc,
      areaServed: "EMEA",
      serviceType: "Software Procurement Consulting",
    },
    {
      "@type": "FAQPage",
      "@id": "https://saased.co/aela#faq",
      mainEntity: faqData.map((item) => ({
        "@type": "Question",
        name: item.question,
        acceptedAnswer: { "@type": "Answer", text: item.answer },
      })),
    },
  ],
};

const buyerChecklist = [
  {
    title: "Contract scope",
    body: "Which products and consumption services are covered, and what sits outside the agreement entirely?",
  },
  {
    title: "Definition of unlimited",
    body: "What exactly is unlimited, and which product-specific conditions, fair-use rules or exclusions apply?",
  },
  {
    title: "Term and renewal",
    body: "What is the initial term, and what renewal mechanics, notice periods and uplift assumptions follow it?",
  },
  {
    title: "Price protection",
    body: "What is genuinely fixed, what can change during the term, and how are new products priced when added?",
  },
  {
    title: "Product evolution",
    body: "How are newly released Salesforce AI and data capabilities treated, and can they be brought inside the framework?",
  },
  {
    title: "Commercial flexibility",
    body: "Can scope be adjusted if adoption lands materially below — or above — the forecast the deal was built on?",
  },
  {
    title: "Exit and transition",
    body: "What happens commercially and operationally at the end of the agreement, and which protections survive it?",
  },
];

const advisory = [
  "Commercial and contract review before signature or renewal",
  "Scope and entitlement analysis against what you actually use",
  "Consumption and scenario modelling across AI and data growth paths",
  "Commercial risk analysis and negotiation strategy",
  "Price protection, renewal planning and long-term commercial governance",
];

const insights = [
  {
    title: "How Agentic Enterprise Licence Agreement Pricing Really Works",
    slug: "how-agentic-enterprise-licence-agreement-pricing-really-works",
  },
  {
    title:
      "Salesforce Agentforce Cost Breakdown: How Flex Credits & Tokens Impact Your Bill",
    slug: "salesforce-agentforce-cost-breakdown-how-flex-credits-and-tokens-impact-your-bill",
  },
  {
    title:
      "Salesforce Agentforce: What Enterprise Leaders Actually Need to Know",
    slug: "salesforce-agentforce-what-enterprise-leaders-actually-need-to-know",
  },
  {
    title: "Is Salesforce AELA the New SELA?",
    slug: "is-salesforce-aela-the-new-sela",
  },
  {
    title:
      "The Blueprint for Salesforce AELA: What Enterprise Buyers Need to Know",
    slug: "the-blueprint-for-salesforce-aela-what-enterprise-buyers-need-to-know",
  },
  {
    title: "Salesforce SELA vs. AELA: Navigating the Agentic AI Cost Paradigm",
    slug: "salesforce-sela-vs-aela-navigating-the-agentic-ai-cost-paradigm",
  },
  {
    title:
      "Salesforce Commercial Structures: Standard Agreement vs. SELA vs. AELA",
    slug: "salesforce-commercial-structures-standard-agreement-vs-sela-vs-aela",
  },
];

const sources = [
  {
    label: "Salesforce — Flexible Buying Models",
    href: "https://www.salesforce.com/pricing/flexible-buying-models/",
  },
  {
    label:
      "Salesforce Investor Relations — VA awards Salesforce $1.6B contract",
    href: "https://investor.salesforce.com/news/news-details/2026/VA-Awards-Salesforce-1-6B-Contract-to-Transform-Veteran-Care-and-Services/default.aspx",
  },
  {
    label:
      "Forrester — AI agents become economic actors: Salesforce rewrites the rules of pricing",
    href: "https://www.forrester.com/blogs/ai-agents-become-economic-actors-salesforce-rewrites-the-rules-of-pricing/",
  },
];

export const Route = createFileRoute("/aela")({
  head: () => ({
    meta: [
      { title: `${pageTitle} | SaaSed` },
      { name: "description", content: searchDescription },
      { property: "og:title", content: `${pageTitle} | SaaSed` },
      { property: "og:description", content: searchDescription },
      { property: "og:type", content: "article" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: `${pageTitle} | SaaSed` },
      { name: "twitter:description", content: searchDescription },
      { property: "og:url", content: "https://saased.co/aela" },
    ],
    links: [{ rel: "canonical", href: "https://saased.co/aela" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(jsonLd),
      },
    ],
  }),
  component: AelaPage,
});

function ArrowIcon({ size = 16 }: { size?: number }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m12 5 7 7-7 7" />
    </svg>
  );
}

const linkClass =
  "underline decoration-2 underline-offset-4 hover:text-brand-punch";

function AelaPage() {
  return (
    <>
      {/* HERO & DIRECT ANSWER */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              Salesforce AELA Procurement &amp; Commercial Advisory
            </p>
            <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-[1.05] tracking-tight max-w-4xl">
              Understanding Agentforce &amp; AELA Agreements
            </h1>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              AELA stands for Agentic Enterprise License Agreement — the
              Salesforce enterprise commercial model at the centre of this page.
            </p>
            <div className="max-w-3xl flex flex-col gap-5">
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                Salesforce&apos;s Agentic Enterprise License Agreement (AELA) is
                a fixed-price, fixed-term commercial agreement for
                enterprise-wide access to covered Salesforce consumption
                services. Under the AELA model, an organisation receives
                unlimited access to those covered services without usage-based
                billing or overage charges, which is intended to give greater
                price predictability as AI and data usage scales.
              </p>
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                Agentforce is one of the Salesforce capabilities most often
                discussed alongside AELA, and it remains an important part of
                the picture. But the agreement — not any single product — is
                what determines your commercial exposure. This page explains the
                AELA model, what it can cover, and what procurement, IT and
                finance should review before committing. It sits inside our
                wider{" "}
                <Link to="/salesforce-procurement" className={linkClass}>
                  Salesforce procurement and commercial advisory
                </Link>{" "}
                practice.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 01 — WHAT IS A SALESFORCE AELA */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              01 · Definition
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              What Is a Salesforce AELA?
            </h2>
            <p
              className="font-sans text-base leading-relaxed text-brand-ink/85"
              style={{ marginBottom: "24px" }}
            >
              An Agentic Enterprise License Agreement is a Salesforce enterprise
              commercial model built for organisations scaling AI and data
              capabilities across the business. Salesforce describes it as
              fixed-price and fixed-term, providing unlimited access to the
              consumption services the agreement covers, with no usage-based
              billing or overage charges under that model.
            </p>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85">
              The important qualifier is &ldquo;covered&rdquo;. An AELA is not a
              blanket entitlement to everything Salesforce sells: the actual
              scope, products and entitlements are defined by the individual
              agreement. Two AELAs signed by two enterprises can look materially
              different in what they include, how long they run, and how new
              capabilities are treated.
            </p>
          </div>
        </div>
      </section>

      {/* 02 — COMMERCIAL ECONOMICS */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              02 · Economics
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              How Does AELA Change Salesforce Commercial Economics?
            </h2>
            <div className="grid md:grid-cols-2 gap-12 md:gap-16 mt-2">
              <div className="flex flex-col gap-5 md:border-r md:border-brand-ink/15 md:pr-8 lg:pr-12">
                <h3 className="font-sans text-lg font-medium">
                  Consumption-based economics
                </h3>
                <ul className="flex flex-col gap-4 font-sans text-base leading-relaxed text-brand-ink/85">
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>Usage is metered against purchased consumption.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>
                      Spend moves with actual activity, so cost follows adoption.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>
                      Forecasting is harder when AI and data workloads grow
                      quickly.
                    </span>
                  </li>
                </ul>
              </div>
              <div className="flex flex-col gap-5 md:pl-8 lg:pl-12">
                <h3 className="font-sans text-lg font-medium">
                  Under an AELA
                </h3>
                <ul className="flex flex-col gap-4 font-sans text-base leading-relaxed text-brand-ink/85">
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>Fixed price for a fixed term.</span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>
                      Unlimited access to the covered consumption services.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>
                      No overage charges or usage-based billing under the model.
                    </span>
                  </li>
                  <li className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>
                      Greater budget predictability across the agreed term.
                    </span>
                  </li>
                </ul>
              </div>
            </div>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl mt-4">
              This shift is not automatic or universal. Not every Salesforce
              product or agreement falls under an AELA, and the predictability
              only extends as far as the scope the contract defines.
              Independent analyst commentary from{" "}
              <a
                href="https://www.forrester.com/blogs/ai-agents-become-economic-actors-salesforce-rewrites-the-rules-of-pricing/"
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                Forrester
              </a>{" "}
              reads AELA as part of a wider move toward flat-rate, unlimited-use
              economics for AI agents and consumption-based software — an
              interpretation of market direction rather than a statement of
              contractual terms.
            </p>
          </div>
        </div>
      </section>

      {/* 03 — AELA AND AGENTFORCE */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              03 · Context
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              AELA and Agentforce
            </h2>
            <p
              className="font-sans text-base leading-relaxed text-brand-ink/85"
              style={{ marginBottom: "24px" }}
            >
              Agentforce is Salesforce&apos;s platform for autonomous AI agents
              and a central part of its current AI ecosystem. It is one of the
              capabilities most commonly relevant inside an AELA, which is why
              the two terms are so often discussed together.
            </p>
            <p
              className="font-sans text-base leading-relaxed text-brand-ink/85"
              style={{ marginBottom: "24px" }}
            >
              The distinction matters commercially. AELA is the agreement;
              Agentforce is a product that may be covered by it. Buying
              Agentforce does not put you under an AELA, and signing an AELA
              does not by itself define how Agentforce is deployed, governed or
              scoped for your organisation.
            </p>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85">
              For a closer look at Agentforce itself, read{" "}
              <Link
                to="/blog/$slug"
                params={{
                  slug: "salesforce-agentforce-what-enterprise-leaders-actually-need-to-know",
                }}
                className={linkClass}
              >
                what enterprise leaders actually need to know about Agentforce
              </Link>
              .
            </p>
          </div>
        </div>
      </section>

      {/* 04 — WHAT CAN AN AELA COVER + FLEX CREDITS */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            <div className="flex flex-col gap-6 md:border-r md:border-brand-ink/15 md:pr-8 lg:pr-12">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
                04 · Scope
              </p>
              <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                What Can an AELA Cover?
              </h2>
              <p
                className="font-sans text-base leading-relaxed text-brand-ink/85"
                style={{ marginBottom: "24px" }}
              >
                Salesforce describes AELA in connection with consumption
                services including Agentforce, Data 360 (Data Cloud) and, in
                enterprise contexts, MuleSoft. The exact products, entitlements
                and scope are defined by the individual agreement.
              </p>
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                No AELA automatically includes every Salesforce AI or data
                product. Treat any coverage list as a starting point for
                negotiation and confirm it line by line in the contract itself.
              </p>
            </div>
            <div className="flex flex-col gap-6 md:pl-8 lg:pl-12">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
                05 · Consumption
              </p>
              <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                Where Do Flex Credits Fit Into AELA?
              </h2>
              <p
                className="font-sans text-base leading-relaxed text-brand-ink/85"
                style={{ marginBottom: "24px" }}
              >
                Flex Credits belong to Salesforce&apos;s broader consumption and
                buying ecosystem, and the Digital Wallet can give visibility
                into how consumption is drawn down across products.
              </p>
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                An AELA is a different instrument: an enterprise agreement
                providing unlimited access to covered consumption services for a
                fixed price and term. AELA is not the same as buying a pool of
                Flex Credits, and how consumption is treated commercially
                depends on your specific agreement.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 06 — REAL-WORLD EXAMPLE */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              06 · In practice
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              AELA in Practice: Salesforce&apos;s $1.6B VA Agreement
            </h2>
            <p
              className="font-sans text-base leading-relaxed text-brand-ink/85"
              style={{ marginBottom: "24px" }}
            >
              Salesforce{" "}
              <a
                href="https://investor.salesforce.com/news/news-details/2026/VA-Awards-Salesforce-1-6B-Contract-to-Transform-Veteran-Care-and-Services/default.aspx"
                target="_blank"
                rel="noopener noreferrer"
                className={linkClass}
              >
                announced a $1.6B agreement with the U.S. Department of Veterans
                Affairs
              </a>{" "}
              structured as an Agentic Enterprise License Agreement. The
              announcement references enterprise-scale AI and data capabilities,
              including Agentforce, Data 360, MuleSoft and related Salesforce
              capabilities.
            </p>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85">
              What it demonstrates is that AELA is now being used as a
              significant enterprise commercial structure, at scale. What it
              does not demonstrate is a template: the VA agreement&apos;s value,
              duration, covered products and commercial terms are specific to
              that deal and should not be assumed to apply to any other AELA.
            </p>
          </div>
        </div>
      </section>

      {/* 07 — BUYER CHECKLIST */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-10">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              07 · Buyer review
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              What Should Enterprise Buyers Review Before Signing an AELA?
            </h2>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              These are the questions to put on the table. They are not claims
              about what a Salesforce AELA contains — every answer below is
              agreement-specific and has to be confirmed in your own contract.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            {buyerChecklist.map((item, idx) => (
              <div
                key={item.title}
                className="flex h-full flex-col gap-3 rounded-2xl border border-brand-ink/10 bg-brand-cream p-8"
              >
                <span className="font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/60">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <h3 className="font-sans text-lg font-medium text-brand-ink">
                  {item.title}
                </h3>
                <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                  {item.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 08 — SAASED ROLE */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20">
          <div className="grid md:grid-cols-2 gap-12 md:gap-16">
            <div className="flex flex-col gap-6 md:border-r md:border-brand-ink/15 md:pr-8 lg:pr-12">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
                08 · Our role
              </p>
              <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                How SaaSed Supports AELA Buyers
              </h2>
              <p
                className="font-sans text-base leading-relaxed text-brand-ink/85"
                style={{ marginBottom: "24px" }}
              >
                A fixed price does not remove the need for procurement
                discipline. Scope, definitions, renewal mechanics and product
                evolution still decide what the agreement is worth over its full
                term.
              </p>
              <ul className="flex flex-col gap-4 font-sans text-base leading-relaxed text-brand-ink/85">
                {advisory.map((item) => (
                  <li key={item} className="flex gap-3">
                    <span className="text-brand-punch mt-1">•</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                We are a Salesforce procurement and commercial advisory firm,
                independent of Salesforce. We are not Salesforce, we do not represent
                Salesforce, and we do not promise a guaranteed saving. See the
                full range of our{" "}
                <Link to="/services" className={linkClass}>
                  Salesforce procurement and negotiation services
                </Link>
                .
              </p>
            </div>
            <div className="flex flex-col gap-6 md:pl-8 lg:pl-12">
              <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
                09 · Comparison
              </p>
              <h2 className="font-serif text-2xl md:text-3xl leading-tight">
                AELA vs. SELA
              </h2>
              <p
                className="font-sans text-base leading-relaxed text-brand-ink/85"
                style={{ marginBottom: "24px" }}
              >
                SELA and AELA are different Salesforce commercial structures. A
                SELA is the enterprise agreement structure historically
                associated with broad, largely seat-based licensing; an AELA is
                built around unlimited access to covered consumption services
                for a fixed price and term.
              </p>
              <p
                className="font-sans text-base leading-relaxed text-brand-ink/85"
                style={{ marginBottom: "24px" }}
              >
                AELA is not a renamed SELA, and it does not automatically
                replace one. Whether a move makes sense — and whether it is
                available at all — depends on your current contract, its timing
                and your AI and data roadmap.
              </p>
              <p className="font-sans text-base leading-relaxed text-brand-ink/85">
                For seat-based enterprise agreements, see our{" "}
                <Link to="/sela" className={linkClass}>
                  Salesforce SELA advisory
                </Link>
                , or read our comparison of{" "}
                <Link
                  to="/blog/$slug"
                  params={{
                    slug: "salesforce-sela-vs-aela-navigating-the-agentic-ai-cost-paradigm",
                  }}
                  className={linkClass}
                >
                  SELA vs. AELA and the agentic AI cost paradigm
                </Link>
                .
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* RELATED INSIGHTS */}
      <section className="border-b border-brand-ink/10 bg-brand-cream">
        <div className="container-x py-16 md:py-20">
          <div className="flex flex-col gap-6 mb-10">
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Salesforce AELA &amp; Agentforce Enterprise Insights
            </h2>
            <p className="font-sans text-base leading-relaxed text-brand-ink/85 max-w-3xl">
              In-depth analysis on Salesforce agentic licensing, consumption
              economics and enterprise commercial strategy. Both AELA and SELA
              sit inside the broader picture of{" "}
              <Link to="/salesforce-procurement" className={linkClass}>
                Salesforce procurement and commercial advisory
              </Link>
              .
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
            {insights.map((article) => (
              <Link
                key={article.slug}
                to="/blog/$slug"
                params={{ slug: article.slug }}
                title={article.title}
                aria-label={`Read ${article.title}`}
                className="group flex h-full flex-col justify-between gap-6 rounded-2xl border border-brand-ink/10 bg-brand-primary p-8 transition-colors hover:border-brand-punch/40"
              >
                <h3 className="font-serif text-xl leading-tight text-brand-ink">
                  {article.title}
                </h3>
                <span className="inline-flex items-center gap-2 font-sans text-xs font-medium uppercase tracking-[0.18em] text-brand-ink/70 group-hover:text-brand-punch">
                  Read the analysis
                  <ArrowIcon />
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-b border-brand-ink/10 bg-[#f0e8c8]">
        <div className="container-x py-24 md:py-28">
          <p className="text-center font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70" style={{ marginBottom: "16px" }}>
            10 · FAQ
          </p>
          <h2 className="m-0 text-center font-serif text-3xl font-bold text-[#1a1408] md:text-4xl">
            FAQ about Salesforce AELA
          </h2>
          <div aria-hidden="true" className="mx-auto mt-4 h-[2px] w-16 bg-[#f5cc00]" />
          <div className="mx-auto mt-12 flex max-w-3xl flex-col gap-4">
            {faqData.map((item) => (
              <details key={item.question} className="group overflow-hidden rounded-2xl border border-[#f0e8c8] bg-[#fffdf0] shadow-[0_1px_2px_rgba(26,20,8,0.06)] transition-colors duration-150 hover:border-[#1a1408]/30">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 px-6 py-5 md:px-8 md:py-6 rounded-2xl focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1a1408] [&::-webkit-details-marker]:hidden">
                  <h3 className="m-0 font-sans text-lg font-medium leading-[1.35] tracking-normal text-[#1a1408] md:text-xl">{item.question}</h3>
                  <span aria-hidden="true" className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-[#f0e8c8] transition-colors duration-200 group-open:bg-[#f5cc00]">
                    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" className="transition-transform duration-200 group-open:rotate-45"><path d="M7 1v12M1 7h12" stroke="#1a1408" strokeWidth="2" strokeLinecap="round" /></svg>
                  </span>
                </summary>
                <div className="flex flex-col gap-4 border-t border-[#f0e8c8] px-6 pb-6 pt-5 md:px-8 md:pb-8">
                  <p className="m-0 text-left text-base leading-[1.7] text-[#1a1408]/85">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* SOURCES */}
      <section className="border-b border-brand-ink/10 bg-brand-primary">
        <div className="container-x py-16 md:py-20 max-w-4xl">
          <div className="flex flex-col gap-6">
            <p className="font-sans text-xs font-medium uppercase tracking-[0.2em] text-brand-ink/70">
              Sources
            </p>
            <h2 className="font-serif text-2xl md:text-3xl leading-tight">
              Sources &amp; Further Reading
            </h2>
            <ul className="flex flex-col gap-4 font-sans text-base leading-relaxed text-brand-ink/85">
              {sources.map((source) => (
                <li key={source.href} className="flex gap-3">
                  <span className="text-brand-punch mt-1">•</span>
                  <a
                    href={source.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={linkClass}
                  >
                    {source.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* END CTA */}
      <DarkCtaSection
        label="Next Step"
        title="Ready to review the commercial terms of your Salesforce AELA?"
        buttonText="Book an AELA Contract Review"
        buttonClassName="rounded-sm"
        sectionClassName="min-h-[440px] md:min-h-[520px]"
      />
    </>
  );
}
